import { Link, useLocation } from "react-router-dom";
import { useEffect, useRef, useCallback } from "react";
// Removed problematic Bootstrap imports. We will access the global 'window.bootstrap' object.

function Navbar() {
  const location = useLocation();
  const isHomePage = location.pathname === "/";
  // Ref for the main navbar collapse div
  const navbarRef = useRef(null);
  // Ref for the dropdown list item element
  const dropdownRef = useRef(null);

  /**
   * Helper function to safely get a Bootstrap instance using the global object,
   * falling back to DOM checks if the instance cannot be retrieved immediately.
   */
  const getBootstrapInstance = (element, componentName) => {
    if (window.bootstrap && window.bootstrap[componentName] && element) {
      // Use the native Bootstrap method to get the instance
      return window.bootstrap[componentName].getInstance(element);
    }
    return null;
  };

  /**
   * Helper function to close the main Bootstrap navbar collapse.
   */
  const collapseNavbar = useCallback(() => {
    const navbarCollapse = navbarRef.current;
    if (navbarCollapse) {
      const bsCollapse = getBootstrapInstance(navbarCollapse, 'Collapse');

      if (bsCollapse) {
        // Use the Bootstrap API to hide the component if the instance is available
        bsCollapse.hide();
      } else if (navbarCollapse.classList.contains("show")) {
        // Fallback: Manual class removal if the instance is not available (less robust)
        navbarCollapse.classList.remove("show");
        const navbarToggle = document.querySelector(".navbar-toggler");
        if (navbarToggle) {
          navbarToggle.setAttribute("aria-expanded", "false");
        }
      }
    }
  }, [navbarRef]); // Included navbarRef, though stable, for completeness

  /**
   * Helper function to close the specific dropdown menu.
   */
  const collapseDropdown = useCallback(() => {
    const dropdownElement = dropdownRef.current;
    if (dropdownElement) {
      const bsDropdown = getBootstrapInstance(dropdownElement, 'Dropdown');

      if (bsDropdown) {
        // Use the Bootstrap API to hide the dropdown if the instance is available
        bsDropdown.hide();
      } else if (dropdownElement.classList.contains("show")) {
        // Fallback: Manual class removal if the instance is not available
        dropdownElement.classList.remove("show");
        const dropdownMenu = dropdownElement.querySelector(".dropdown-menu");
        if (dropdownMenu) dropdownMenu.classList.remove("show");
        const dropdownToggle = dropdownElement.querySelector(".dropdown-toggle");
        if (dropdownToggle) dropdownToggle.setAttribute("aria-expanded", "false");
      }
    }
  }, [dropdownRef]); // Included dropdownRef, though stable, for completeness


  // Effect 1: Auto-collapse main navbar on route change (for mobile responsiveness)
  useEffect(() => {
    // Collapse the main navbar when the route changes
    collapseNavbar();
    // Also ensure any open dropdown is closed on route change
    collapseDropdown();
  }, [location.pathname, collapseNavbar, collapseDropdown]);

  // Effect 2: Auto-collapse main navbar on outside click (for mobile responsiveness)
  useEffect(() => {
    const handleClickOutside = (event) => {
      const navbarToggle = document.querySelector(".navbar-toggler");

      // Only collapse the main navbar if the click is outside the navbar and the toggler
      if (
        navbarRef.current &&
        !navbarRef.current.contains(event.target) &&
        navbarToggle &&
        !navbarToggle.contains(event.target)
      ) {
        collapseNavbar();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [collapseNavbar]);

  // Handler to close the main Navbar collapse (used for regular nav links)
  const handleLinkClick = () => {
    collapseNavbar();
  };

  // Handler to close both the main Navbar Collapse (mobile) AND the Dropdown Menu (desktop)
  const handleDropdownItemClick = () => {
    // 1. Close the main Navbar Collapse (handles mobile/tablet menu closure)
    collapseNavbar();

    // 2. Explicitly close the Dropdown Menu (handles desktop menu closure)
    collapseDropdown();
  };

  const awsImageBaseUrl = process.env.REACT_APP_AWS_IMAGE_BUCKET_URL;

  return (
    <nav className="navbar navbar-expand-lg navbar-dark text-white">
      <div className="container-fluid">
        {/* Logo link, visible everywhere except desktop home page */}
        <Link className={`navbar-brand col-2 col-md-1 ${isHomePage ? "d-lg-none" : "d-block"}`} to="/">
          {/* Using a placeholder image for runnable code, replace with actual usage if needed */}
          <img src={awsImageBaseUrl ? `${awsImageBaseUrl}images/BrandAcumen_Logo.png` : "https://placehold.co/100x40/000000/FFFFFF?text=Logo"} alt="Logo Graphic" className="img-fluid col-7 col-md-5" />
        </Link>

        {/* Navbar Toggler Button (Mobile) */}
        <button className="navbar-toggler d-lg-none" type="button" data-bs-toggle="collapse" data-bs-target="#navbarNav">
          <span className="navbar-toggler-icon"></span>
        </button>

        {/* Navbar Links Container */}
        <div className="collapse navbar-collapse justify-content-center" id="navbarNav" ref={navbarRef}>
          <ul className="navbar-nav">
            {/* Regular Nav Links */}
            <li className="nav-item mx-5"><Link className="nav-link" to="/people-purpose" onClick={handleLinkClick}>People & Purpose</Link></li>
            <li className="nav-item mx-5"><Link className="nav-link" to="/what-we-do" onClick={handleLinkClick}>What We Do</Link></li>
            <li className="nav-item mx-5"><Link className="nav-link" to="/brand-vault" onClick={handleLinkClick}>Brand Vault</Link></li>

            {/* Dropdown Nav Item */}
            <li className="nav-item dropdown mx-5" ref={dropdownRef}>
              <a className="nav-link dropdown-toggle" href="/clients" id="clientsLink" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                Clients
              </a>
              <ul className="dropdown-menu text-white" aria-labelledby="clientsLink">
                <li><Link className="dropdown-item" to="/clients" onClick={handleDropdownItemClick}>All Clients</Link></li>
                <li><hr className="dropdown-divider" /></li>
                <li><Link className="dropdown-item" to="/clients/auto" onClick={handleDropdownItemClick}>Auto</Link></li>
                <li><Link className="dropdown-item" to="/clients/cpg" onClick={handleDropdownItemClick}>CPG</Link></li>
                <li><Link className="dropdown-item" to="/clients/healthcare" onClick={handleDropdownItemClick}>Healthcare</Link></li>
                <li><Link className="dropdown-item" to="/clients/ce-tech" onClick={handleDropdownItemClick}>CE & Tech</Link></li>
                <li><Link className="dropdown-item" to="/clients/specialty" onClick={handleDropdownItemClick}>Specialty</Link></li>
              </ul>
            </li>

            <li className="nav-item mx-5"><Link className="nav-link" to="/work-with-us" onClick={handleLinkClick}>Work With Us</Link></li>
          </ul>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;
